import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  Bell, 
  BellOff, 
  Clock, 
  Users, 
  TrendingUp, 
  Calendar,
  Zap,
  Moon,
  Brain,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getNotificationSettings,
  updateNotificationSettings,
  requestNotificationPermission,
  areNotificationsEnabled,
  sendTestNotification,
  learnOptimalReminderTime,
  getNotificationHistory,
  rescheduleNotifications,
  type NotificationSettings,
} from '../lib/notification-service';
import { MotionDiv, fadeIn } from '../lib/motion-utils';
import { NotificationStats } from '../components/NotificationStats';

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<NotificationSettings>(getNotificationSettings());
  const [permissionState, setPermissionState] = useState<NotificationPermission>('default');
  const [history, setHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [optimalTime, setOptimalTime] = useState<string | null>(null);
  const subscription = localStorage.getItem('tension_subscription') || 'free';
  const isPremium = subscription !== 'free';

  useEffect(() => {
    if ('Notification' in window) {
      // DEFENSIVE: Wrap Notification API access in try-catch
      try {
        setPermissionState(Notification.permission);
      } catch (error) {
        console.debug('Notification API error:', error);
        setPermissionState('denied');
      }
    }
    setHistory(getNotificationHistory());

    learnOptimalReminderTime();
    const learned = localStorage.getItem('tension_optimal_reminder_time');
    if (learned) {
      setOptimalTime(learned);
    }
  }, []);

  const handleToggle = (key: keyof NotificationSettings) => {
    const newSettings = { ...settings, [key]: !settings[key as keyof NotificationSettings] };
    setSettings(newSettings);
    updateNotificationSettings(newSettings);
    toast.success('Settings updated');
  };

  const handleTimeChange = (key: 'reminderTime' | 'quietHoursStart' | 'quietHoursEnd', value: string) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    updateNotificationSettings(newSettings);
    rescheduleNotifications();
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setPermissionState(granted ? 'granted' : 'denied');

    if (granted) {
      toast.success('Notifications enabled!');
      setTimeout(() => sendTestNotification(), 500);
      setSettings(getNotificationSettings());
    } else {
      toast.error('Notification permission denied. Check your browser settings.');
    }
  };

  const handleApplyOptimalTime = () => {
    if (optimalTime) {
      handleTimeChange('reminderTime', optimalTime);
      toast.success(`Reminder time updated to ${optimalTime} based on your patterns`);
    }
  };

  const ToggleRow = ({ 
    icon: Icon, 
    title, 
    description, 
    settingKey, 
    isPremiumFeature = false,
    children 
  }: { 
    icon: any; 
    title: string; 
    description: string; 
    settingKey: keyof NotificationSettings;
    isPremiumFeature?: boolean;
    children?: React.ReactNode;
  }) => {
    const isDisabled = isPremiumFeature && !isPremium;
    const value = settings[settingKey];
    
    return (
      <div className={`space-y-4 ${isDisabled ? 'opacity-50' : ''}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Icon className={`w-5 h-5 mt-0.5 ${isDisabled ? 'text-neutral-400' : 'text-neutral-600'}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Label className="text-base font-medium">{title}</Label>
                {isPremiumFeature && (
                  <Crown className="w-4 h-4 text-amber-500" />
                )}
              </div>
              <p className="text-sm text-neutral-600 mt-1">{description}</p>
              {isPremiumFeature && !isPremium && (
                <p className="text-xs text-blue-600 mt-1">Premium Feature</p>
              )}
            </div>
          </div>
          <Switch
            checked={typeof value === 'boolean' ? value : false}
            onCheckedChange={() => handleToggle(settingKey)}
            disabled={isDisabled}
          />
        </div>
        {children && typeof value === 'boolean' && value && (
          <div className="ml-8 space-y-3">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-light">Notification Settings</h1>
            <p className="text-sm text-neutral-600">
              {areNotificationsEnabled() ? 'Notifications enabled' : 'Notifications disabled'}
            </p>
          </div>
          {areNotificationsEnabled() && (
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Permission Card */}
        {permissionState !== 'granted' && (
          <MotionDiv {...fadeIn}>
            <Card className="p-6 bg-blue-50 border-blue-200">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Bell className="w-6 h-6 text-blue-600" />
                  <h2 className="text-lg font-medium">Enable Notifications</h2>
                </div>
                <p className="text-sm text-neutral-700">
                  Get gentle reminders to complete your daily check-in and stay aware of tension patterns. 
                  All notifications are sent locally from your device - your data never leaves your phone.
                </p>
                <Button
                  onClick={handleEnableNotifications}
                  className="w-full"
                  size="lg"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Browser Notifications
                </Button>
                {permissionState === 'denied' && (
                  <div className="tension-glow flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-800">
                      Notifications are blocked. Please enable them in your browser settings.
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </MotionDiv>
        )}

        {/* Core Notifications */}
        {areNotificationsEnabled() && (
          <>
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-1">Daily Check-In</h3>
                <p className="text-sm text-neutral-600">Essential for building the check-in habit</p>
              </div>
              
              <Separator />
              
              <ToggleRow
                icon={Clock}
                title="Daily Reminder"
                description="Get reminded to complete your daily tension score check-in"
                settingKey="dailyReminder"
              >
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">Reminder Time</Label>
                  <input
                    type="time"
                    value={settings.reminderTime}
                    onChange={(e) => handleTimeChange('reminderTime', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {optimalTime && optimalTime !== settings.reminderTime && (
                    <div className="tension-glow flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <Brain className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-amber-900">
                          Based on your check-in patterns, we suggest <strong>{optimalTime}</strong> as your optimal reminder time.
                        </p>
                        <Button
                          onClick={handleApplyOptimalTime}
                          variant="outline"
                          size="sm"
                          className="mt-2 h-7 text-xs"
                        >
                          Apply Suggested Time
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </ToggleRow>

              <Separator />

              <ToggleRow
                icon={Brain}
                title="Adaptive Timing"
                description="Automatically learn your optimal reminder time based on when you typically check in"
                settingKey="adaptiveTiming"
              />
            </Card>

            {/* Streak & Partner */}
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-1">Motivation & Coordination</h3>
                <p className="text-sm text-neutral-600">Stay motivated and in sync with your partner</p>
              </div>
              
              <Separator />
              
              <ToggleRow
                icon={Sparkles}
                title="Streak Milestones"
                description="Celebrate your check-in streaks (3, 7, 14, 30 days)"
                settingKey="streakReminders"
              />

              <Separator />

              <ToggleRow
                icon={Users}
                title="Partner Sync"
                description="Get notified when your partner completes their check-in"
                settingKey="partnerSync"
              />
            </Card>

            {/* Tension & Insights */}
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-1">Tension Awareness</h3>
                <p className="text-sm text-neutral-600">Stay aware of important pattern changes</p>
              </div>
              
              <Separator />
              
              <ToggleRow
                icon={TrendingUp}
                title="High Tension Alerts"
                description="Get notified when your tension score enters High Tension"
                settingKey="highTensionAlerts"
              />

              <Separator />

              <ToggleRow
                icon={AlertCircle}
                title="Forecast Alerts"
                description="Advanced alerts when tension spikes are predicted"
                settingKey="forecastAlerts"
                isPremiumFeature={true}
              />
            </Card>

            {/* Action & Reports */}
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-1">Actions & Reports</h3>
                <p className="text-sm text-neutral-600">Reminders for recommended actions</p>
              </div>
              
              <Separator />
              
              <ToggleRow
                icon={Zap}
                title="Action Card Reminders"
                description="Get reminded about your daily action recommendations"
                settingKey="actionReminders"
              />

              <Separator />

              <ToggleRow
                icon={Zap}
                title="Micro-Action Nudges"
                description="Tiny interventions throughout the day based on your score patterns"
                settingKey="microActionReminders"
              />

              <Separator />

              <ToggleRow
                icon={Calendar}
                title="Weekly Summary"
                description="Receive your weekly pattern report every Sunday morning"
                settingKey="weeklyReport"
              />
            </Card>

            {/* Quiet Hours */}
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-1">Quiet Hours</h3>
                <p className="text-sm text-neutral-600">Pause notifications during specific times</p>
              </div>
              
              <Separator />
              
              <ToggleRow
                icon={Moon}
                title="Enable Quiet Hours"
                description="No notifications will be sent during these hours"
                settingKey="quietHoursEnabled"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-neutral-700">Start Time</Label>
                    <input
                      type="time"
                      value={settings.quietHoursStart}
                      onChange={(e) => handleTimeChange('quietHoursStart', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-neutral-700">End Time</Label>
                    <input
                      type="time"
                      value={settings.quietHoursEnd}
                      onChange={(e) => handleTimeChange('quietHoursEnd', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </ToggleRow>
            </Card>

            {/* Notification Statistics */}
            <NotificationStats />

            {/* Notification History */}
            {history.length > 0 && (
              <Card className="p-6">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="text-left">
                    <h3 className="font-medium">Recent Notifications</h3>
                    <p className="text-sm text-neutral-600">Last {history.length} notifications</p>
                  </div>
                  <ArrowLeft className={`w-5 h-5 text-neutral-400 transition-transform ${showHistory ? '-rotate-90' : 'rotate-180'}`} />
                </button>

                {showHistory && (
                  <div className="mt-4 space-y-2">
                    {history.slice(-10).reverse().map((item, index) => (
                      <div key={index} className="p-3 bg-neutral-50 rounded-lg">
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-neutral-600 mt-1">{item.body}</p>
                        <p className="text-xs text-neutral-400 mt-1">
                          {new Date(item.deliveredAt).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Test Notification */}
            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium">Test Notifications</h3>
                  <p className="text-sm text-neutral-600">Send yourself a test notification</p>
                </div>
                <Button
                  onClick={() => {
                    sendTestNotification();
                    toast.success('Test notification sent!');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Send Test Notification
                </Button>
              </div>
            </Card>

            {/* Privacy Note */}
            <Card className="p-4 bg-green-50 border-green-200">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-green-900">Privacy-First Notifications</p>
                  <p className="text-xs text-green-800">
                    All notifications are generated and sent locally from your device. TENSION does not use 
                    external push notification servers. Your relationship data never leaves your phone.
                  </p>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}